import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ImagePlus, Loader2, LogOut, Trash2, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { authHeader, useAuth } from "@/lib/auth";
import { LOGO } from "@/lib/brand";

const emptyForm = {
  title: "",
  category: "",
  client: "",
  description: "",
  project_date: "",
  published: true,
  sort_order: 0,
};

export default function AdminPortfolio() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [coverFile, setCoverFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/portfolio", { headers: authHeader() });
      setItems(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Could not load portfolio.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadImage = async (file) => {
    const body = new FormData();
    body.append("file", file);
    const { data } = await api.post("/admin/media/upload-image", body, {
      headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
    });
    return data;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.category.trim()) {
      toast.error("Project title and category are required.");
      return;
    }
    setSaving(true);
    try {
      let cover = null;
      let images = [];
      if (coverFile) cover = await uploadImage(coverFile);
      if (galleryFiles.length) images = await Promise.all(galleryFiles.map(uploadImage));
      await api.post("/admin/portfolio", {
        ...form,
        sort_order: Number(form.sort_order) || 0,
        cover,
        images,
      }, { headers: authHeader() });
      toast.success("Project published to portfolio.");
      setForm(emptyForm);
      setCoverFile(null);
      setGalleryFiles([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (item) => {
    try {
      const payload = {
        title: item.title,
        category: item.category,
        client: item.client || null,
        description: item.description || null,
        project_date: item.project_date || null,
        cover: item.cover || null,
        images: item.images || [],
        published: !item.published,
        sort_order: item.sort_order || 0,
      };
      await api.put(`/admin/portfolio/${item.id}`, payload, { headers: authHeader() });
      await load();
    } catch {
      toast.error("Could not update project.");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete “${item.title}” from the portfolio?`)) return;
    try {
      await api.delete(`/admin/portfolio/${item.id}`, { headers: authHeader() });
      toast.success("Project deleted.");
      await load();
    } catch {
      toast.error("Could not delete project.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-black paper-grain">
      <header className="sticky top-0 z-40 bg-[#F4F4F0]/95 backdrop-blur border-b border-black">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO.mark} alt="Arvind Printers" className="h-10 w-auto" />
            <div className="hidden sm:block">
              <div className="font-display text-lg leading-none">Arvind Printers</div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-black/60 mt-1">Admin · Portfolio</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/admin/quotes" className="btn-offset">Quotes</Link>
            <Link to="/admin/reels" className="btn-offset">Reels</Link>
            <span className="hidden lg:block font-mono text-[10px] tracking-[0.18em] uppercase text-black/60">{user?.email}</span>
            <button className="btn-offset" onClick={() => { logout(); navigate("/admin/login", { replace: true }); }}><LogOut className="w-3.5 h-3.5" /> Log out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] px-6 md:px-10 py-10 md:py-14">
        <div className="mb-10">
          <div className="font-mono text-xs tracking-[0.24em] uppercase text-black/60">Media Centre / Our Work</div>
          <h1 className="font-display text-5xl md:text-6xl leading-none mt-2">Portfolio Manager</h1>
          <p className="mt-4 max-w-2xl text-black/70">Upload finished jobs here. Published projects will later feed the public Selected Work gallery automatically.</p>
        </div>

        <div className="grid lg:grid-cols-[430px_1fr] gap-8 items-start">
          <form onSubmit={submit} className="border border-black p-5 md:p-6 bg-white/35 lg:sticky lg:top-28">
            <div className="font-mono text-xs tracking-[0.2em] uppercase mb-5">Add project</div>
            <div className="space-y-4">
              <Field label="Project title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="Luxury menu printing" />
              <Field label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} placeholder="Offset Printing / Packaging" />
              <Field label="Client (optional)" value={form.client} onChange={(v) => setForm({ ...form, client: v })} placeholder="Client name" />
              <Field label="Date / Year" value={form.project_date} onChange={(v) => setForm({ ...form, project_date: v })} placeholder="September 2026" />
              <label className="block"><span className="label">Description</span><textarea className="input min-h-28" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Materials, finishing, quantity, special process…" /></label>
              <label className="block"><span className="label">Cover photo</span><input className="input py-2" type="file" accept="image/*" onChange={(e) => setCoverFile(e.target.files?.[0] || null)} /></label>
              <label className="block"><span className="label">Gallery photos</span><input className="input py-2" type="file" accept="image/*" multiple onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))} /><small className="block mt-1 text-black/55">Select several photos at once.</small></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="label">Display order</span><input className="input" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} /></label>
                <label className="flex items-end gap-2 pb-3 font-mono text-xs uppercase"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Publish now</label>
              </div>
              <button disabled={saving} className="btn-offset w-full justify-center py-3">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload & publish</>}</button>
            </div>
          </form>

          <section>
            <div className="flex items-center justify-between border-b border-black pb-3 mb-4"><div className="font-mono text-xs tracking-[0.2em] uppercase">Existing projects</div><div className="font-mono text-xs text-black/55">{items.length} total</div></div>
            {loading ? <div className="py-20 flex justify-center"><Loader2 className="animate-spin" /></div> : items.length === 0 ? (
              <div className="border border-dashed border-black/40 py-24 text-center"><ImagePlus className="mx-auto mb-3" /><p>No projects yet. Upload your first completed job.</p></div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {items.map((item) => <article key={item.id} className="border border-black bg-white/25 overflow-hidden">
                  <div className="aspect-[4/3] bg-black/5 overflow-hidden">{item.cover?.url ? <img src={item.cover.url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-black/35"><ImagePlus /></div>}</div>
                  <div className="p-4">
                    <div className="font-mono text-[11px] tracking-[0.15em] uppercase text-black/55">{item.category} {item.client ? `· ${item.client}` : ""}</div>
                    <h2 className="font-display text-2xl mt-1">{item.title}</h2>
                    <p className="text-sm text-black/65 mt-2 line-clamp-2">{item.description || "No description"}</p>
                    <div className="flex gap-2 mt-4">
                      <button className="btn-offset flex-1 justify-center" onClick={() => togglePublished(item)}>{item.published ? "Published" : "Hidden"}</button>
                      <button className="btn-offset" onClick={() => remove(item)} aria-label="Delete project"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </article>)}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return <label className="block"><span className="label">{label}</span><input className="input" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} /></label>;
}
